import { getElementTypesById } from "core-integration/src/store/element-types/reducers";
import { IElement } from "core-integration/src/store/elements/types";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { integerToMoney } from "core-integration/src/utils/money";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { getElementShirtPath } from "./ElementShirt";

const Card = styled.div`
  padding: 2px;
  background-color: ${({ theme }) => theme.colors.blueDarker3};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const PlayerWrapper = styled.div`
  order: 0;
  width: 50%;
  padding: 30px 0;
  box-sizing: border-box;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: auto;
    padding: ${({ theme }) => theme.space[3]};
  }
`;

const Img = styled.img`
  display: block;
  margin: auto;
  max-width: 120px;
  width: 100%;
  height: auto;
  padding: ${({ theme }) => theme.space[2]};
  border-radius: ${({ theme }) => theme.radii[2]};
  background-color: ${({ theme }) => theme.colors.white};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 140px;
  }
`;

const BadgeWrapper = styled.div`
  width: 50%;
  order: 1;
  padding: 30px 0;
  box-sizing: border-box;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    order: 3;
    width: auto;
    padding: ${({ theme }) => theme.space[3]};
  }
`;

const CardInfo = styled.div`
  order: 3;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    flex: 1 0 auto;
    order: 1;
    width: auto;
  }
`;

const ElementInfo = styled.ul`
  margin: 0;
  padding: 0;
  list-style-type: none;
  border: ${({ theme }) => theme.borders[0]};
  border-radius: ${({ theme }) => theme.radii[2]};
`;

const ElementInfoItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
  border-bottom: ${({ theme }) => theme.borders[0]};

  &:last-child {
    border: 0;
  }
`;

const ElementInfoHeading = styled.h3`
  margin: 0;
  padding: 10px;
  width: 50%;
  font-size: 14px;
  line-height: 1;
  border-right: ${({ theme }) => theme.borders[0]};
`;

const ElementInfoValue = styled.div`
  padding: 10px;
  width: 50%;
  font-size: 14px;
  line-height: 1;
  color: ${({ theme }) => theme.colors.yellow};
`;

interface IProps {
  element: IElement;
}

const ElementCard = ({ element }: IProps) => {
  const { t } = useTranslation();
  const teams = useSelector(getTeamsById);
  const elementTypesById = useSelector(getElementTypesById);
  if (!element) return null;

  const elementFullname = `${element.first_name} ${element.second_name}`;
  const elementType = elementTypesById[element.element_type];
  const elementCost = `${integerToMoney(element.now_cost, 10)}Mkr`;
  return (
    <Card>
      <PlayerWrapper>
        <Img
          src={`https://allsvenskan.se/wp-content/uploads/2026/03/${element.code}.png`}
          alt={t(
            "elementDialog.tabs.summary.playerImageAlt",
            "Player image for {{name}}",
            { name: elementFullname },
          )}
          role="presentation"
          onError={(e) => {
            e.currentTarget.src = `${getElementShirtPath(
              element.element_type,
              teams[element.team].code,
              "standard",
            )}-110.png`;
          }}
        />
      </PlayerWrapper>
      <BadgeWrapper>
        <Img
          src={`/img/badges/badge_${element.team_code}.png`}
          alt={t(
            "elementDialog.tabs.summary.teamBadgeAlt",
            "Football club badge for {{name}}",
            { name: teams[element.team].name },
          )}
          role="presentation"
          onError={(e) => {
            e.currentTarget.src = `${getElementShirtPath(
              element.element_type,
              teams[element.team].code,
              "standard",
            )}-110.png`;
          }}
        />
      </BadgeWrapper>
      <CardInfo>
        <ElementInfo>
          <ElementInfoItem>
            <ElementInfoHeading>
              {t("elementDialog.tabs.summary.position", "Position")}:
            </ElementInfoHeading>
            <ElementInfoValue>{elementType.singular_name}</ElementInfoValue>
          </ElementInfoItem>
          <ElementInfoItem>
            <ElementInfoHeading>
              {t("elementDialog.tabs.summary.value", "Value")}:
            </ElementInfoHeading>
            <ElementInfoValue>{elementCost}</ElementInfoValue>
          </ElementInfoItem>
          <ElementInfoItem>
            <ElementInfoHeading>
              {t("elementDialog.tabs.summary.selectedBy", "Selected by")}:
            </ElementInfoHeading>
            <ElementInfoValue>{element.selected_by_percent}%</ElementInfoValue>
          </ElementInfoItem>
          <ElementInfoItem>
            <ElementInfoHeading>
              {t("elementDialog.tabs.summary.totalScore", "Total score")}:
            </ElementInfoHeading>
            <ElementInfoValue>{element.total_points}</ElementInfoValue>
          </ElementInfoItem>
        </ElementInfo>
      </CardInfo>
    </Card>
  );
};

export default ElementCard;
